import { lazy } from 'react';

type ImportFactory<T extends React.ComponentType<any>> = () => Promise<{ default: T }>;

const CHUNK_UPDATE_EVENT = 'app-chunk-update-detected';

const isChunkLoadError = (error: unknown) => {
  const name = typeof error === 'object' && error ? String((error as { name?: unknown }).name || '') : '';
  const message = typeof error === 'object' && error ? String((error as { message?: unknown }).message || '') : '';
  return /ChunkLoadError/i.test(name) || /Loading chunk [\s\S]+ failed/i.test(message);
};

const notifyChunkUpdate = (payload: { key: string; message?: string }) => {
  window.dispatchEvent(new CustomEvent(CHUNK_UPDATE_EVENT, { detail: payload }));
};

const importWithRetry = async <T extends React.ComponentType<any>>(
  factory: ImportFactory<T>,
  key: string,
): Promise<{ default: T }> => {
  try {
    const module = await factory();
    return module;
  } catch (error) {
    if (isChunkLoadError(error)) {
      notifyChunkUpdate({
        key,
        message: typeof error === 'object' && error ? String((error as { message?: unknown }).message || '') : '',
      });
    }

    throw error;
  }
};

const lazyWithRetry = <T extends React.ComponentType<any>>(
  factory: ImportFactory<T>,
  key: string,
) => {
  return lazy(() => importWithRetry(factory, key));
};

export { CHUNK_UPDATE_EVENT, isChunkLoadError, notifyChunkUpdate };
export default lazyWithRetry;
